import { useQuery } from '@tanstack/react-query';

import { Skeleton } from '@/components/Skeleton/Skeleton';
import { Tabs, TabsList, TabsTrigger } from '@/components/Tabs/Tabs';
import { type ClientsParams } from '@/features/reports/api';
import { ClientMixView } from '@/features/reports/clients/ClientMixView';
import { CyclesView } from '@/features/reports/clients/CyclesView';
import { DropOffView } from '@/features/reports/clients/DropOffView';
import { InactiveView } from '@/features/reports/clients/InactiveView';
import { SpendView } from '@/features/reports/clients/SpendView';
import { CLIENTS_COPY as COPY, REPORTS_COPY, TRANSACTIONS_COPY } from '@/features/reports/copy';
import { ManagerFilter, PeriodControl, ReportFailure } from '@/features/reports/parts';
import { type ReportPeriod } from '@/features/reports/periods';
import { reportClientsQuery } from '@/features/reports/queries';
import { CLIENTS_VIEW, CLIENTS_VIEWS, type ClientsView } from '@/features/reports/tabs';

/**
 * Reports › Clients (ALS-87): five views behind one tab, one per ticket rule, as Transactions puts
 * its reports behind sub-tabs. All five are one request for the period, the inactive window and
 * the relationship manager, so they load and fail as one and moving between them asks nothing
 * again. The relationship manager scopes the whole tab to her clients, so a stylist sees her own
 * (Franka, 22 Oct 2026); it is the page's, the same choice as the order queue's RM.
 */
export function ClientsPanel({
    view,
    params,
    onViewChange,
    onPeriodChange,
    onManagerChange,
    onWindowChange,
}: {
    view: ClientsView;
    params: ClientsParams;
    onViewChange: (view: ClientsView) => void;
    onPeriodChange: (period: ReportPeriod) => void;
    onManagerChange: (rmId: number | undefined) => void;
    onWindowChange: (days: number | undefined) => void;
}) {
    const report = useQuery(reportClientsQuery(params));
    const data = report.data;

    return (
        <div className="flex min-w-0 flex-col gap-(--layout-row-gap)">
            <Tabs value={view} onValueChange={(next) => onViewChange(next as ClientsView)}>
                {/* Five labels do not fit a 320 phone: the strip scrolls inside itself, fades the
                    side with more past it and keeps the chosen view in sight (ALS-263). */}
                <TabsList
                    size="sm"
                    scrollable
                    aria-label={TRANSACTIONS_COPY.tabs.clients}
                    className="self-start"
                >
                    {CLIENTS_VIEWS.map((v) => (
                        <TabsTrigger key={v} value={v}>
                            {COPY.views[v]}
                        </TabsTrigger>
                    ))}
                </TabsList>
            </Tabs>

            <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex min-w-0 flex-wrap items-center gap-3">
                    <PeriodControl period={params.period} onPeriodChange={onPeriodChange} />
                    <ManagerFilter rmId={params.rmId} onManagerChange={onManagerChange} />
                </div>
                {data ? (
                    <span className="text-muted-foreground text-xs">
                        {REPORTS_COPY.hint(data.current.label, data.previous.label)}
                    </span>
                ) : null}
            </div>

            {report.isPending ? (
                <div
                    aria-busy="true"
                    aria-label="Loading the report"
                    className="flex flex-col gap-(--layout-row-gap)"
                >
                    <div className="grid grid-cols-2 gap-3 xl:grid-cols-4">
                        {Array.from({ length: 4 }, (_, i) => (
                            <Skeleton key={i} className="h-[84px] rounded-lg" />
                        ))}
                    </div>
                    <Skeleton className="h-[320px] rounded-md" />
                </div>
            ) : report.isError && !data ? (
                <ReportFailure copy={COPY.failed} onRetry={() => void report.refetch()} />
            ) : data ? (
                view === CLIENTS_VIEW.Mix ? (
                    <ClientMixView report={data} params={params} />
                ) : view === CLIENTS_VIEW.Inactive ? (
                    <InactiveView report={data} params={params} onWindowChange={onWindowChange} />
                ) : view === CLIENTS_VIEW.DropOff ? (
                    <DropOffView report={data} params={params} />
                ) : view === CLIENTS_VIEW.Cycles ? (
                    <CyclesView report={data} params={params} />
                ) : (
                    <SpendView report={data} params={params} />
                )
            ) : null}
        </div>
    );
}
